import { useState, useEffect } from 'react';
import { alarmProfilesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Sliders, Trash2, Edit, Star, Zap } from 'lucide-react';

const profileTypeLabels = {
  lenient: { label: 'Mild', color: 'bg-green-100 text-green-800' },
  medium: { label: 'Balanserad', color: 'bg-blue-100 text-blue-800' },
  strict: { label: 'Strikt', color: 'bg-red-100 text-red-800' },
  custom: { label: 'Anpassad', color: 'bg-slate-100 text-slate-800' },
};

export default function AlarmProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [creatingPredefined, setCreatingPredefined] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    is_default: false,
    pm1_delta: 0,
    pm2_5_delta: 0,
    pm10_delta: 0,
    voc_delta: 0,
    humidity_delta: 0,
    suspect_threshold: 0.5,
    vape_threshold: 0.8,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await alarmProfilesApi.list();
      setProfiles(response.data);
    } catch (error) {
      toast.error('Kunde inte ladda larmprofiler');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProfile) {
        await alarmProfilesApi.update(editingProfile.id, formData);
        toast.success('Profil uppdaterad');
      } else {
        await alarmProfilesApi.create(formData);
        toast.success('Profil skapad');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Är du säker på att du vill ta bort denna profil?')) return;
    try {
      await alarmProfilesApi.delete(id);
      toast.success('Profil borttagen');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte ta bort profil');
    }
  };

  const handleCreatePredefined = async () => {
    setCreatingPredefined(true);
    try {
      const response = await alarmProfilesApi.createPredefined();
      toast.success(`${response.data.message}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte skapa fördefinierade profiler');
    } finally {
      setCreatingPredefined(false);
    }
  };

  const openEditDialog = (profile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      is_default: profile.is_default,
      pm1_delta: profile.pm1_delta,
      pm2_5_delta: profile.pm2_5_delta,
      pm10_delta: profile.pm10_delta,
      voc_delta: profile.voc_delta,
      humidity_delta: profile.humidity_delta,
      suspect_threshold: profile.suspect_threshold,
      vape_threshold: profile.vape_threshold,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProfile(null);
    setFormData({
      name: '',
      is_default: false,
      pm1_delta: 0,
      pm2_5_delta: 0,
      pm10_delta: 0,
      voc_delta: 0,
      humidity_delta: 0,
      suspect_threshold: 0.5,
      vape_threshold: 0.8,
    });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar profiler...</div>
      </div>
    );
  }

  // Check if predefined profiles already exist
  const hasPredefined = profiles.some(p => ['lenient', 'medium', 'strict'].includes(p.profile_type));

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="alarm-profiles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
            Larmprofiler
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            Konfigurera detekteringströsklar och känslighet
          </p>
        </div>
        
        <div className="flex gap-2">
          {!hasPredefined && (
            <Button
              variant="outline"
              onClick={handleCreatePredefined}
              disabled={creatingPredefined}
              className="font-ibm"
              data-testid="create-predefined-btn"
            >
              <Zap className="h-4 w-4 mr-2" />
              {creatingPredefined ? 'Skapar...' : 'Skapa Standardprofiler'}
            </Button>
          )}
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-navy hover:bg-navy/90 rounded-sm font-ibm" data-testid="add-profile-btn">
                <Plus className="h-4 w-4 mr-2" />
                Lägg till profil
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {editingProfile ? 'Redigera Larmprofil' : 'Skapa Larmprofil'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Profilnamn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Hög känslighet"
                  required
                  className="bg-slate-50"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                <div>
                  <Label className="font-ibm">Ställ in som standard</Label>
                  <p className="text-xs text-slate-500">Tillämpas automatiskt på nya enheter</p>
                </div>
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-ibm font-semibold text-sm mb-3">Delta-trösklar</h4>
                <p className="text-xs text-slate-500 mb-3">Ställ in 0 för att använda AI-baserad baslinjdetektion</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'pm1_delta', label: 'PM1', unit: 'µg/m³' },
                    { key: 'pm2_5_delta', label: 'PM2.5', unit: 'µg/m³' },
                    { key: 'pm10_delta', label: 'PM10', unit: 'µg/m³' },
                    { key: 'voc_delta', label: 'VOC', unit: 'ppb' },
                    { key: 'humidity_delta', label: 'Humidity', unit: '%' },
                  ].map((field) => (
                    <div key={field.key} className="space-y-1">
                      <Label className="text-xs text-slate-500">
                        {field.label} ({field.unit})
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData[field.key]}
                        onChange={(e) => setFormData({ ...formData, [field.key]: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-50"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-ibm font-semibold text-sm mb-3">Poängtrösklar</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Misstänkt-tröskel</Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={formData.suspect_threshold}
                      onChange={(e) => setFormData({ ...formData, suspect_threshold: parseFloat(e.target.value) || 0.5 })}
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Vape-tröskel</Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={formData.vape_threshold}
                      onChange={(e) => setFormData({ ...formData, vape_threshold: parseFloat(e.target.value) || 0.8 })}
                      className="bg-slate-50"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-navy rounded-sm" data-testid="profile-submit-btn">
                {editingProfile ? 'Uppdatera Profil' : 'Skapa Profil'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Profiles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profiles.map((profile) => {
          const typeInfo = profileTypeLabels[profile.profile_type] || profileTypeLabels.custom;
          return (
          <Card key={profile.id} className="border-slate-200" data-testid={`profile-card-${profile.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm bg-slate-100 flex items-center justify-center">
                    <Sliders className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-ibm font-semibold text-slate-900">{profile.name}</h3>
                      {profile.is_default && (
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                      {profile.is_default && (
                        <span className="text-xs text-amber-600">Standard</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-500">Suspect @</span>
                  <span className="font-ibm font-semibold tabular-nums">{(profile.suspect_threshold * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Vape @</span>
                  <span className="font-ibm font-semibold tabular-nums">{(profile.vape_threshold * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">PM2.5 Δ</span>
                  <span className="font-ibm tabular-nums">{profile.pm2_5_delta || 'AI'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">VOC Δ</span>
                  <span className="font-ibm tabular-nums">{profile.voc_delta || 'AI'}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditDialog(profile)}
                  data-testid={`edit-profile-${profile.id}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Redigera
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(profile.id)}
                  disabled={profile.is_default}
                  data-testid={`delete-profile-${profile.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {profiles.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Sliders className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-slate-700">Inga larmprofiler</h3>
            <p className="text-slate-500 mt-1 font-ibm">
              Skapa en profil för att konfigurera detekteringskänslighet
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
